/**
 * 🤖 Mock API para InnovaTech con IA Orquestada por Eventos
 * Implementa los 4 roles IA según especificaciones del experto:
 * - tutor: Validación conceptual y orientación estratégica
 * - sparring_cliente: Simulaciones de entrevista (3 perfiles)
 * - analista: Interpretación de métricas y experimentos
 * - evaluador: Calificación con rúbrica estructurada
 */

// 🎯 Configuración de IA según prompt del experto
const AI_CONFIG = {
  global: {
    language: "es-PE",
    rate_limit: { per_session_per_min: 6 },
    max_tokens: 600,
    temperature: 0.4,
    style_rules: [
      "Sé ejecutivo, directo y pedagógico",
      "No resuelvas por el alumno; guía con preguntas concretas", 
      "Prioriza fortaleza → mejora → acción",
      "Máximo 120–160 palabras por respuesta salvo evaluaciones"
    ]
  },
  hypothesis_template: {
    format: "Creemos que [grupo de clientes] tienen [problema] porque [razón observable]",
    min_length: 120,
    description: "Formato obligatorio para hipótesis cliente-problema con mínimo 120 caracteres"
  },
  roles: {
    tutor: {
      max_words: 160,
      structure: "1) Fortaleza, 2) Mejora, 3) Acción sugerida"
    },
    sparring_cliente: {
      max_turns: 7,
      personas: {
        cliente_esceptico: { tone: "parco, duda del valor, exige evidencia" },
        cliente_entusiasta: { tone: "abierto, colaborativo, sugiere casos de uso" },
        cliente_desinteresado: { tone: "distante, ocupado" }
      }
    },
    analista: {
      max_words: 180,
      sections: ["Resumen de métricas", "Interpretación", "Siguiente experimento"]
    },
    evaluador: {
      output_type: "json_rubrica",
      criteria: ["claridad", "evidencia", "tecnico", "modelo"]
    }
  }
};

// 🎯 Dispatcher: Asigna rol IA según lab_id y step_id
const AI_DISPATCHER = {
  rules: [
    { lab_id: "lab1", step_id: "define_hypothesis", role: "tutor" },
    { lab_id: "lab1", step_id: "bml_circuit", role: "tutor" },
    { lab_id: "lab2", step_id: "simulacion_ia", role: "sparring_cliente" },
    { lab_id: "lab3", step_id: "definir_metricas", role: "tutor" },
    { lab_id: "lab3", step_id: "simulacion_trafico", role: "analista" },
    { lab_id: "lab4", step_id: "resultados", role: "analista" },
    { lab_id: "lab4", step_id: "decision", role: "tutor" },
    { lab_id: "lab5", step_id: "panel_rubrica", role: "evaluador" }
  ],
  fallback_role: "tutor"
};

/**
 * 🧠 Determina qué rol de IA usar según contexto
 */
function getAIRole(labId, stepId, action) {
  // Buscar regla específica
  const rule = AI_DISPATCHER.rules.find(r => 
    r.lab_id === labId && r.step_id === stepId
  );
  
  if (rule) {
    console.log(`🎯 [AI Dispatcher] Usando rol: ${rule.role} para ${labId}/${stepId}`);
    return rule.role;
  }
  
  // Reglas adicionales por acción
  if (action === 'delimitar_segmento' || action === 'refinar_hipotesis') {
    return 'tutor';
  }
  
  if (action === 'try_sparring' || action.includes('sparring')) {
    return 'sparring_cliente';
  }
  
  console.log(`🎯 [AI Dispatcher] Usando fallback: ${AI_DISPATCHER.fallback_role}`);
  return AI_DISPATCHER.fallback_role;
}

// Mock database - simulación de respuestas del coach IA
const mockResponses = {
  lab1: {
    enviar_respuesta: {
      "mensajes": [
        {
          "tipo_visual": "feedback_estrategico",
          "generated_by_ai": true,
          "ai_type": "tutor",
          "ai_role": "tutor",
          "ai_limit": 1,
          "ai_current": 1,
          "mensaje_coach": {
            "titulo": "🎓 Análisis del Tutor IA - Validación de Hipótesis",
            "cuerpo": "**Template esperado:** Creemos que [grupo de clientes] tienen [problema] porque [razón observable]\n\nTu hipótesis ha sido procesada. Análisis personalizado basado en tu input se mostrará aquí."
          },
          "opciones": [
            { "etiqueta": "Delimitar segmento", "valor": "delimitar_segmento", "icono": "Target", "color": "blue" },
            { "etiqueta": "Refinar hipótesis", "valor": "refinar_hipotesis", "icono": "Edit", "color": "orange" },
            { "etiqueta": "Pasar al circuito BML", "valor": "ir_bml", "icono": "Workflow", "color": "green" },
            { "etiqueta": "Simular entrevista", "valor": "try_sparring", "icono": "Users", "color": "purple" },
            { "etiqueta": "Nuevo análisis", "valor": "enviar_respuesta", "icono": "RotateCcw", "color": "gray" }
          ],
          "mostrar_input_text": false
        }
      ],
      "next_step": null, // No auto-advance, usuario debe elegir
      "checkpoints": ["hipotesis_analyzed"]
    },
    
    guardar_hipotesis: {
      "mensajes": [
        {
          "tipo_visual": "feedback_estrategico",
          "mensaje_coach": {
            "titulo": "Hipótesis guardada",
            "cuerpo": "Tu hipótesis ha sido guardada. Continuando automáticamente al siguiente paso del laboratorio."
          },
          "opciones": [],
          "mostrar_input_text": false
        }
      ],
      "next_step": "bml_map",
      "checkpoints": ["hipotesis_saved"],
      "auto_advance": true
    },
    
    delimitar_segmento: {
      "mensajes": [
        {
          "tipo_visual": "formulario_breve",
          "mensaje_coach": {
            "titulo": "Refina tu segmento objetivo",
            "cuerpo": "Define: ¿Qué tamaño de empresa? ¿Qué industria específica? ¿Qué rango de ingresos?"
          },
          "opciones": [
            { "etiqueta": "Guardar segmento", "valor": "guardar_segmento", "icono": "save" }
          ],
          "mostrar_input_text": true
        }
      ],
      "next_step": "segmento_refinado"
    },

    refinar_hipotesis: {
      "mensajes": [
        {
          "tipo_visual": "formulario_breve",
          "generated_by_ai": true,
          "ai_type": "single-shot",
          "ai_limit": 1,
          "ai_current": 1,
          "mensaje_coach": {
            "titulo": "🔧 Refinamiento de Hipótesis - Sugerencias IA",
            "cuerpo": "Perfecciona tu hipótesis considerando:\n\n• **Segmento más específico**: ¿Edad, ubicación, comportamiento?\n• **Problema más concreto**: ¿Cuándo ocurre? ¿Qué lo desencadena?\n• **Causa más observable**: ¿Qué evidencia tienes?\n\nReescribe tu hipótesis mejorada:"
          },
          "opciones": [
            { "etiqueta": "Guardar hipótesis refinada", "valor": "guardar_hipotesis_refinada", "icono": "Save", "color": "blue" },
            { "etiqueta": "Volver a la anterior", "valor": "volver_hipotesis_anterior", "icono": "ArrowLeft", "color": "gray" }
          ],
          "mostrar_input_text": true
        }
      ],
      "next_step": null
    },

    ir_bml: {
      "mensajes": [
        {
          "tipo_visual": "narrativa",
          "mensaje_coach": {
            "titulo": "🔄 Circuito Build-Measure-Learn",
            "cuerpo": "Excelente! Ahora que tienes tu hipótesis, es momento de diseñar un experimento para validarla.\n\n**¿Qué es el BML?**\nEs un ciclo de aprendizaje validado que te permite:\n\n🏗️ **Build**: Construir un MVP (Producto Mínimo Viable)\n📊 **Measure**: Medir resultados con métricas clave\n🧠 **Learn**: Aprender y decidir: pivotar o perseverar\n\n**Tu siguiente paso**: Definir qué experimento realizarás para validar tu hipótesis."
          },
          "opciones": [
            { "etiqueta": "Definir experimento", "valor": "definir_experimento", "icono": "flask", "color": "green" },
            { "etiqueta": "Ver ejemplo BML", "valor": "ver_ejemplo_bml", "icono": "book", "color": "orange" },
            { "etiqueta": "Continuar sin experimento", "valor": "continuar_sin_experimento", "icono": "arrow-right", "color": "gray" }
          ],
          "mostrar_input_text": false
        }
      ],
      "next_step": "bml_design"
    },

    guardar_hipotesis_refinada: {
      "mensajes": [
        {
          "tipo_visual": "feedback_estrategico",
          "mensaje_coach": {
            "titulo": "✅ Hipótesis Refinada Guardada",
            "cuerpo": "Perfecto! Tu hipótesis refinada muestra mayor claridad y especificidad. Esto aumentará tus posibilidades de validación exitosa.\n\n**Próximos pasos disponibles:**"
          },
          "opciones": [
            { "etiqueta": "Delimitar segmento", "valor": "delimitar_segmento", "icono": "target", "color": "blue" },
            { "etiqueta": "Ir al circuito BML", "valor": "ir_bml", "icono": "workflow", "color": "green" },
            { "etiqueta": "Finalizar por ahora", "valor": "save_hypothesis", "icono": "save", "color": "gray" }
          ],
          "mostrar_input_text": false
        }
      ],
      "next_step": null
    },

    guardar_segmento: {
      "mensajes": [
        {
          "tipo_visual": "feedback_estrategico", 
          "generated_by_ai": true,
          "ai_type": "analista",
          "ai_limit": 1,
          "ai_current": 1,
          "mensaje_coach": {
            "titulo": "🎯 Segmento Objetivo Definido - Análisis IA",
            "cuerpo": "Excelente! Has delimitado tu segmento objetivo con mayor precisión. Esto te ayudará a:\n\n✅ Enfocar mejor tus experimentos\n✅ Definir métricas más relevantes\n✅ Optimizar el uso de recursos\n\n**¿Qué sigue?**"
          },
          "opciones": [
            { "etiqueta": "Refinar hipótesis", "valor": "refinar_hipotesis", "icono": "Edit", "color": "orange" },
            { "etiqueta": "Ir al circuito BML", "valor": "ir_bml", "icono": "Workflow", "color": "green" },
            { "etiqueta": "Simular entrevista", "valor": "try_sparring", "icono": "Users", "color": "purple" },
            { "etiqueta": "Guardar y continuar", "valor": "save_hypothesis", "icono": "Save", "color": "blue" }
          ],
          "mostrar_input_text": false
        }
      ],
      "next_step": null
    },

    // 🎭 Ejemplo de IA sparring acotado (para futuros laboratorios)
    try_sparring: {
      "mensajes": [
        {
          "tipo_visual": "sparring_chat",
          "generated_by_ai": true,
          "ai_type": "sparring",
          "ai_limit": 5,
          "ai_current": 1,
          "sparring_profile": "cliente_esceptico",
          "mensaje_coach": {
            "titulo": "🎭 Simulación de Entrevista - Cliente Escéptico",
            "cuerpo": "**Perfil simulado:** Cliente potencial con dudas sobre tu propuesta\n\n**Escenario:** Vas a presentar tu hipótesis y el cliente te hará preguntas difíciles\n\n**Límite:** 5 intercambios máximo\n\n---\n\n**Cliente:** \"Hmm, no estoy convencido de que este problema sea tan relevante como dices. ¿Tienes datos reales que lo respalden?\""
          },
          "opciones": [
            { "etiqueta": "Responder con datos", "valor": "sparring_response", "icono": "BarChart", "color": "blue" },
            { "etiqueta": "Dar ejemplo específico", "valor": "sparring_response", "icono": "Lightbulb", "color": "orange" },
            { "etiqueta": "Terminar simulación", "valor": "end_sparring", "icono": "Square", "color": "red" }
          ],
          "mostrar_input_text": true,
          "placeholder": "Escribe tu respuesta al cliente escéptico..."
        }
      ],
      "next_step": null
    },

    show_example_hypothesis: {
      "mensajes": [
        {
          "tipo_visual": "ejemplo_guiado",
          "mensaje_coach": {
            "titulo": "💡 Ejemplo de Hipótesis Validada",
            "cuerpo": "## 🎯 Ejemplo Perfecto de Hipótesis\n\n**Creemos que** *estudiantes universitarios de carreras técnicas* **tienen** *dificultades para encontrar prácticas profesionales relevantes* **porque** *las plataformas actuales no filtran oportunidades por especialización y las empresas publican requisitos genéricos.*\n\n---\n\n### ✅ ¿Por qué esta hipótesis funciona?\n\n**🎯 Segmento específico:** \n- \"estudiantes universitarios de carreras técnicas\" \n- No dice solo \"estudiantes\" (muy amplio)\n\n**🔍 Problema claro:**\n- \"dificultades para encontrar prácticas profesionales relevantes\"\n- Es específico y medible\n\n**📊 Causa observable:**\n- \"plataformas no filtran por especialización\"\n- \"empresas publican requisitos genéricos\"\n- Son causas que se pueden verificar\n\n---\n\n### 📝 Tu turno\nUsa esta estructura para crear tu hipótesis:\n**Creemos que** [grupo específico] **tienen** [problema concreto] **porque** [razón observable]"
          },
          "opciones": [
            { "etiqueta": "Entendido, escribir mi hipótesis", "valor": "volver_formulario", "icono": "edit" }
          ],
          "mostrar_input_text": false
        }
      ],
      "next_step": null
    },

    volver_formulario: {
      "mensajes": [
        {
          "tipo_visual": "formulario_breve",
          "mensaje_coach": {
            "titulo": "Tu turno: Define tu hipótesis",
            "cuerpo": "Usa la plantilla: **Creemos que** [grupo de clientes] **tienen** [problema] **porque** [razón observable]."
          },
          "opciones": [
            { "etiqueta": "Enviar Respuesta", "valor": "enviar_respuesta", "icono": "send", "color": "green" },
            { "etiqueta": "Guardar y continuar", "valor": "save_hypothesis", "icono": "save", "color": "blue" }
          ],
          "mostrar_input_text": true
        }
      ],
      "next_step": null
    }
  }
};

/**
 * 🔄 Sistema de Resilencia según especificaciones del experto
 */
const RESILIENCE_CONFIG = {
  validate_before_call: ["non_empty_input_when_required", "schema_match", "gating_ok"],
  fallback_local: "Si el modelo falla o tarda >8s, devuelve texto de ejemplo/feedback desde coachLogic",
  retry: { policy: "exponential_backoff", max_retries: 2 },
  timeout: 8000 // 8 segundos máximo
};

/**
 * 🔍 Validaciones pre-llamada según experto
 */
function validateBeforeCall(payload) {
  const { opcion_valor, payload: userPayload } = payload;
  const errors = [];
  
  // 1. non_empty_input_when_required
  const requiresInput = ['enviar_respuesta', 'save_hypothesis', 'refinar_hipotesis'].includes(opcion_valor);
  if (requiresInput && (!userPayload?.hipotesis || userPayload.hipotesis.trim() === '')) {
    errors.push('EMPTY_INPUT: Se requiere hipótesis para esta acción');
  }
  
  // 2. schema_match  
  if (userPayload?.hipotesis && userPayload.hipotesis.length > 500) {
    errors.push('SCHEMA_INVALID: Hipótesis excede 500 caracteres');
  }
  
  // 3. gating_ok (simulado)
  // En implementación real, validaría permisos y límites de usuario
  
  return {
    valid: errors.length === 0,
    errors: errors
  };
}

/**
 * 🔨 Simulador de respuesta IA - Solo para testing/mock
 * Los prompts reales están centralizados en prompt_inovatech.json (backend)
 */
function buildMockResponse(role, context) {
  const { hipotesis, labId, stepId } = context;
  
  // 📝 NOTA: En producción se usa prompt_inovatech.json del backend
  // Esta función solo simula respuestas para testing sin backend
  
  console.log(`[🧪 MOCK] Simulando respuesta para rol: ${role}, paso: ${stepId}`);
  
  return {
    simulatedPrompt: `Mock prompt para ${role} - Datos: ${hipotesis?.substring(0, 50) || 'Sin datos'}...`,
    note: "Prompts reales centralizados en JSON backend"
  };
}

/**
 * 🔄 Fallback local según especificaciones de resilencia
 */
function getFallbackResponse(role, context) {
  const { hipotesis } = context;
  
  switch (role) {
    case 'tutor':
      return `**🔄 Análisis Offline Disponible**

Tu hipótesis "${hipotesis.substring(0, 50)}..." ha sido recibida.

**✅ Fortaleza:** Input registrado correctamente.
**🔧 Mejora:** Sistema IA temporalmente no disponible - usa las opciones disponibles.  
**🎯 Acción sugerida:** Delimita segmento o refina hipótesis manualmente.

*Fallback local activado - funcionalidad completa restaurándose...*`;

    case 'sparring_cliente':
      return "Hmm, no puedo conectar ahora. ¿Puedes enviarme la información por email y la reviso después?";

    case 'analista':
      return `**📊 Análisis Básico Disponible**

• Hipótesis registrada: ✅
• Análisis IA: ⏳ Procesando...  
• Próximo experimento: Refinar segmento objetivo

*Sistema analítico restaurándose - usa opciones manuales mientras tanto.*`;

    default:
      return getFallbackResponse('tutor', context);
  }
}

/**
 * 🤖 Llamada a IA real con OpenAI
 */
async function callRealAI(prompt, role, context) {
  console.log(`🧠 [IA Real] Activando rol: ${role}`);
  
  try {
    // 🚀 ACTIVAR IA REAL - Importar servicio dinámicamente
    const { getAIResponse } = await import('./aiService.js');
    
    const aiResult = await getAIResponse(role, context);
    
    if (aiResult.success) {
      console.log(`✅ [IA Real] ${role} completado exitosamente`);
      return aiResult.content;
    } else {
      console.warn(`⚠️ [IA Real] ${role} usó fallback:`, aiResult.error);
      return aiResult.content; // Fallback content
    }
    
  } catch (error) {
    console.error('❌ Error cargando servicio IA:', error);
    
    // Fallback de emergencia si no se puede cargar el servicio
    return await simulateIntelligentResponse(prompt, role, context);
  }
}

/**
 * 🎯 Simula respuesta inteligente (temporal hasta integrar OpenAI)
 */
async function simulateIntelligentResponse(prompt, role, context) {
  // Simular procesamiento IA
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  const { labId, hipotesis } = context;
  
  switch (role) {
    case 'tutor':
      return tutorAnalysis(hipotesis, 'define_hypothesis');
    case 'sparring_cliente':
      return sparringClienteResponse(hipotesis, 'cliente_esceptico', 1);
    case 'analista':
      return analistaMetrics({ hipotesis }, 'segmentation');
    default:
      return tutorAnalysis(hipotesis, 'define_hypothesis');
  }
}

/**
 * 🔄 POST /api/innovatech/session/advance con IA Real y Resilencia
 */
export const mockSessionAdvance = async (payload) => {
  console.log('🚀 [IA Service] Procesando con IA real:', payload);
  
  // 🔍 Validaciones pre-llamada
  const validation = validateBeforeCall(payload);
  if (!validation.valid) {
    console.warn('⚠️ Validación fallida:', validation.errors);
    return {
      error: {
        code: 'VALIDATION_FAILED',
        message: validation.errors.join(', '),
        recoverable: true
      }
    };
  }
  
  // Simular latencia de red reducida
  await new Promise(resolve => setTimeout(resolve, 500));
  
  const { session_id, opcion_valor, payload: userPayload } = payload;
  
  // Extraer lab_id del session_id simulado
  const labId = session_id?.includes('lab') ? session_id.split('_')[0] : 'lab1';
  
  // Buscar respuesta en mock database
  const labResponses = mockResponses[labId];
  const response = labResponses?.[opcion_valor];
  
  if (!response) {
    console.warn('⚠️ Mock: Opción no encontrada:', opcion_valor);
    return {
      error: {
        code: 'OPTION_NOT_FOUND',
        message: `Opción '${opcion_valor}' no implementada en mock`,
        hint: 'Verifica que la opción esté en mockResponses',
        recoverable: true
      }
    };
  }
  
  // 🧠 IA Real con dispatcher y resilencia
  const personalizedResponse = { ...response };
  
  if (userPayload?.hipotesis) {
    console.log('📝 Hipótesis recibida para IA real:', userPayload.hipotesis);
    
    try {
      // Extraer contexto para el dispatcher
      const context = {
        labId: labId,
        stepId: 'define_hypothesis', // Por ahora, expandir según necesidad
        sessionId: session_id,
        hipotesis: userPayload.hipotesis
      };
      
      // 🎯 Determinar rol de IA
      const aiRole = getAIRole(context.labId, context.stepId, opcion_valor);
      console.log(`🤖 [IA] Activando rol: ${aiRole}`);
      
      // 🚀 Mock para testing - Los prompts reales están en JSON backend
      const mockPrompt = buildMockResponse(aiRole, context);
      
      let feedback;
      let attempts = 0;
      const maxRetries = RESILIENCE_CONFIG.retry.max_retries;
      
      while (attempts <= maxRetries) {
        try {
          // Race condition: IA vs Timeout
          const aiPromise = callRealAI(mockPrompt, aiRole, context);
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('TIMEOUT')), RESILIENCE_CONFIG.timeout)
          );
          
          feedback = await Promise.race([aiPromise, timeoutPromise]);
          break; // Éxito
          
        } catch (error) {
          attempts++;
          console.warn(`⚠️ Intento ${attempts}/${maxRetries + 1} falló:`, error.message);
          
          if (attempts > maxRetries) {
            console.log('🔄 Usando fallback local...');
            feedback = getFallbackResponse(aiRole, context);
          } else {
            // Exponential backoff
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempts) * 1000));
          }
        }
      }
      
      // Actualizar metadata de IA real
      personalizedResponse.mensajes[0].mensaje_coach.cuerpo = feedback;
      personalizedResponse.mensajes[0].ai_role = aiRole;
      personalizedResponse.mensajes[0].generated_by_ai = true;
      personalizedResponse.mensajes[0].ai_type = aiRole;
      personalizedResponse.mensajes[0].ai_source = feedback.includes('Fallback') ? 'fallback' : 'openai';
      
    } catch (error) {
      console.error('❌ Error crítico en IA:', error);
      
      // Fallback de emergencia
      const emergencyFallback = "⚠️ **Sistema IA temporalmente no disponible**\n\nTu hipótesis ha sido recibida. Por favor, intenta nuevamente en unos momentos o usa las opciones disponibles para continuar.";
      personalizedResponse.mensajes[0].mensaje_coach.cuerpo = emergencyFallback;
      personalizedResponse.mensajes[0].ai_source = 'emergency_fallback';
    }
  }
  
  // Personalizar según hipótesis previa para refinamientos
  if (userPayload?.hipotesis_previa && opcion_valor === 'refinar_hipotesis') {
    console.log('🔧 Refinando hipótesis:', userPayload.hipotesis_previa);
    const refinementSuggestions = generateRefinementSuggestions(userPayload.hipotesis_previa);
    personalizedResponse.mensajes[0].mensaje_coach.cuerpo = refinementSuggestions;
  }
  
  console.log('✅ Mock Response:', personalizedResponse);
  return personalizedResponse;
};

/**
 * 🎓 ROL TUTOR: Validación conceptual según template del experto
 */
function tutorAnalysis(hypothesis, context = 'define_hypothesis') {
  const template = AI_CONFIG.hypothesis_template.format;
  const minLength = AI_CONFIG.hypothesis_template.min_length;
  const text = hypothesis.toLowerCase();
  
  let feedback = "";
  
  // 1) Validación de formato según template
  const hasCreemos = text.includes('creemos que') || text.includes('creo que');
  const hasTienen = text.includes('tienen') || text.includes('tiene');  
  const hasPorque = text.includes('porque') || text.includes('ya que');
  
  // 2) Estructura: Fortaleza → Mejora → Acción
  
  // FORTALEZA
  if (hasCreemos && hasTienen && hasPorque) {
    feedback += "**✅ Fortaleza:** Sigue la estructura correcta del template.\n\n";
  } else {
    feedback += "**⚠️ Formato incompleto:** ";
    if (!hasCreemos) feedback += "Inicia con 'Creemos que'. ";
    if (!hasTienen) feedback += "Especifica que el grupo 'tienen' un problema. ";
    if (!hasPorque) feedback += "Explica 'porque' [razón observable]. ";
    feedback += "\n\n";
  }
  
  // MEJORA
  feedback += "**🔧 Mejora crítica:** ";
  if (text.length < minLength) {
    feedback += `Tu hipótesis debe tener al menos ${minLength} caracteres para garantizar suficiente detalle y contexto.`;
  } else if (!text.includes('empresa') && !text.includes('cliente') && !text.includes('usuario')) {
    feedback += "Define mejor tu grupo de clientes objetivo.";
  } else {
    feedback += "Agrega métricas observables (tiempo, costo, frecuencia).";
  }
  feedback += "\n\n";
  
  // ACCIÓN SUGERIDA
  feedback += "**🎯 Acción sugerida:** ";
  if (hasCreemos && hasTienen && hasPorque && text.length >= minLength) {
    feedback += "Delimita tu segmento o pasa al circuito BML para experimentar.";
  } else {
    feedback += "Reescribe usando: 'Creemos que [grupo específico] tienen [problema medible] porque [razón que puedes observar]'.";
  }
  
  return feedback;
}

/**
 * 🎭 ROL SPARRING_CLIENTE: Simulación de entrevista
 */
function sparringClienteResponse(userInput, persona = 'cliente_esceptico', turn = 1) {
  const personas = AI_CONFIG.roles.sparring_cliente.personas;
  const profile = personas[persona];
  
  let response = "";
  
  if (persona === 'cliente_esceptico') {
    const responses = [
      "No estoy convencido. ¿Tienes números reales que respalden eso?",
      "Suena caro. ¿Cuánto me costaría implementarlo?",
      "Ya probé algo similar y no funcionó. ¿Qué tiene de diferente?",
      "¿Por qué no puedo seguir haciendo lo que hago ahora?",
      "Mi equipo está ocupado. ¿Realmente vale la pena el tiempo?"
    ];
    response = responses[Math.min(turn - 1, responses.length - 1)];
  } else if (persona === 'cliente_entusiasta') {
    const responses = [
      "¡Interesante! ¿Podrías usarlo también para nuestro proceso de ventas?",
      "Me gusta la idea. ¿Qué necesitarías de nosotros para probarlo?",
      "Tengo un colega que también podría necesitar esto. ¿Cuándo estaría listo?",
      "¿Tienes casos de empresas similares a la nuestra?",
      "¿Cómo podríamos medir el impacto real?"
    ];
    response = responses[Math.min(turn - 1, responses.length - 1)];
  } else { // cliente_desinteresado
    const responses = [
      "Ajá. ¿Y eso en cuánto tiempo?",
      "Mmm. Tenemos otras prioridades ahora mismo.",
      "¿Puedes enviarme la información por email?",
      "Está bien. ¿Algo más?",
      "Vale, lo revisaré cuando tenga tiempo."
    ];
    response = responses[Math.min(turn - 1, responses.length - 1)];
  }
  
  return response;
}

/**
 * 📊 ROL ANALISTA: Interpretación de métricas 
 */
function analistaMetrics(metrics, context = 'segmentation') {
  let analysis = "";
  
  analysis += "**📊 Resumen de métricas:**\n";
  analysis += "• Segmento refinado con criterios específicos\n";
  analysis += "• Problema validado en contexto universitario\n\n";
  
  analysis += "**🔍 Interpretación:**\n";
  analysis += "El refinamiento indica mayor claridad en el público objetivo, lo cual mejorará la precisión de futuros experimentos.\n\n";
  
  analysis += "**🚀 Siguiente experimento:**\n";
  analysis += "**Variable:** Tasa de resonancia del problema\n";
  analysis += "**Criterio:** >60% confirma el dolor específico\n";
  analysis += "**Muestra mínima:** 10-15 entrevistas\n";
  
  return analysis;
}

/**
 * 🤖 Función principal de análisis IA con dispatcher
 */
function analyzeHypothesis(hypothesis, actionType = 'enviar_respuesta', context = {}) {
  const { labId = 'lab1', stepId = 'define_hypothesis' } = context;
  const aiRole = getAIRole(labId, stepId, actionType);
  
  console.log(`🧠 [IA] Usando rol: ${aiRole} para análisis`);
  
  switch (aiRole) {
    case 'tutor':
      return tutorAnalysis(hypothesis, stepId);
    case 'sparring_cliente':
      return sparringClienteResponse(hypothesis, 'cliente_esceptico', 1);
    case 'analista':
      return analistaMetrics({ hypothesis }, stepId);
    default:
      return tutorAnalysis(hypothesis, stepId); // Fallback
  }
  const minLength = AI_CONFIG.hypothesis_template.min_length;
  const keywords = {
    good: ['necesita', 'problema', 'dolor', 'dificultad', 'mejora', 'requiere', 'carece'],
    segment: ['empresa', 'pyme', 'startup', 'usuario', 'cliente', 'estudiante', 'profesional'],
    solution: ['app', 'plataforma', 'sistema', 'herramienta', 'servicio', 'chatbot', 'software'],
    metrics: ['tiempo', 'costo', 'satisfacción', 'eficiencia', 'productividad', '%', 'reducir', 'aumentar']
  };
  
  const text = hypothesis.toLowerCase();
  
  // 🎯 Respuesta breve del coach según modelo "single-shot"
  let feedback = "🎯 **Análisis IA - Respuesta Única**\n\n";
  
  // Análisis de estructura
  const hasGoodWords = keywords.good.some(word => text.includes(word));
  const hasSegment = keywords.segment.some(word => text.includes(word));
  const hasSolution = keywords.solution.some(word => text.includes(word));
  const hasMetrics = keywords.metrics.some(word => text.includes(word));
  
  // Puntaje de calidad universitario
  let quality = 0;
  if (hasGoodWords) quality += 25;
  if (hasSegment) quality += 25;
  if (hasMetrics) quality += 25;
  if (text.length >= minLength && text.length < 300) quality += 25;
  
  // Evaluación pedagógica
  let grade = 'F';
  if (quality >= 90) grade = 'A+';
  else if (quality >= 85) grade = 'A';
  else if (quality >= 80) grade = 'B+';
  else if (quality >= 75) grade = 'B';
  else if (quality >= 70) grade = 'C+';
  else if (quality >= 60) grade = 'C';
  else if (quality >= 50) grade = 'D';
  
  feedback += `**📊 Evaluación Académica:** ${grade} (${quality}/100)\n`;
  feedback += `**🎓 Nivel:** ${quality >= 75 ? 'Universitario avanzado' : quality >= 60 ? 'Universitario básico' : 'Necesita desarrollo'}\n\n`;
  
  // Feedback directo y accionable
  if (quality >= 75) {
    feedback += "✅ **¡Excelente trabajo!** Tu hipótesis muestra:\n";
    if (hasGoodWords) feedback += "• Problema bien definido\n";
    if (hasSegment) feedback += "• Segmento target claro\n";
    if (hasMetrics) feedback += "• Enfoque cuantificable\n\n";
  } else {
    feedback += "🔧 **Áreas críticas a mejorar:**\n";
    if (!hasSegment) feedback += "• **SEGMENTO**: Especifica tu público objetivo\n";
    if (!hasGoodWords) feedback += "• **PROBLEMA**: Define el dolor específico\n";
    if (!hasMetrics) feedback += "• **MÉTRICAS**: Agrega números medibles\n\n";
  }
  
  // CTA siguiente paso según modelo del experto
  feedback += "🚀 **Acciones recomendadas:**\n";
  feedback += "• **Delimitar segmento** → Mayor precisión de mercado\n";
  feedback += "• **Refinar hipótesis** → Incorporar sugerencias IA\n";
  feedback += "• **Circuito BML** → Diseñar experimentos\n\n";
  
  feedback += `💡 **Esta es tu única respuesta IA.** ${quality < 60 ? 'Considera refinar antes de continuar.' : 'Puedes usar "Reintentar análisis" si necesitas nueva perspectiva.'}`;
  
  return feedback;
}

/**
 * Genera sugerencias específicas para refinamiento de hipótesis
 */
function generateRefinementSuggestions(previousHypothesis) {
  const text = previousHypothesis.toLowerCase();
  
  let suggestions = "🔧 **Sugerencias Personalizadas para Refinamiento**\n\n";
  suggestions += `**Tu hipótesis anterior:**\n"${previousHypothesis}"\n\n`;
  
  suggestions += "**💡 Mejoras sugeridas por IA:**\n\n";
  
  // Análisis de segmento
  if (!text.includes('edad') && !text.includes('industria') && !text.includes('tamaño')) {
    suggestions += "🎯 **Segmento**: Especifica demografía:\n";
    suggestions += "• Edad: ¿18-25, 30-45, 50+?\n";
    suggestions += "• Industria: ¿Tecnología, retail, salud?\n";
    suggestions += "• Tamaño: ¿Startup, PYME, corporación?\n\n";
  }
  
  // Análisis de problema
  if (!text.includes('cuando') && !text.includes('donde')) {
    suggestions += "🔍 **Contexto del problema**: Agrega cuándo/dónde ocurre:\n";
    suggestions += "• ¿En qué momento específico surge?\n";
    suggestions += "• ¿En qué situaciones es más crítico?\n\n";
  }
  
  // Análisis de métricas
  if (!text.includes('%') && !text.includes('tiempo') && !text.includes('costo')) {
    suggestions += "📊 **Métricas**: Incluye números específicos:\n";
    suggestions += "• ¿Cuánto tiempo se pierde actualmente?\n";
    suggestions += "• ¿Qué % de mejora esperas?\n";
    suggestions += "• ¿Cuál es el costo del problema actual?\n\n";
  }
  
  suggestions += "✨ **Plantilla mejorada:**\n";
  suggestions += '**Creemos que** [segmento específico + demografía] **tienen** [problema concreto + contexto] **porque** [causa observable + evidencia] **y esto les genera** [impacto medible].\n\n';
  
  suggestions += "🎯 **Ahora reescribe tu hipótesis usando estas mejoras:**";
  
  return suggestions;
}

/**
 * 🔍 Valida que la sugerencia siga la estructura correcta "Creemos que X tienen Y porque Z"
 * @param {Object} suggestion - Sugerencia con group, problem, reason
 * @returns {Object} Sugerencia validada y corregida si es necesario
 */
function validateAndFixSuggestion(suggestion) {
  const { group, problem, reason } = suggestion;
  
  // Verificar que 'problem' sea un sustantivo, no un verbo
  const verbsToFix = [
    { verb: /^detectan/i, noun: 'detección de' },
    { verb: /^pierden/i, noun: 'pérdida de' },
    { verb: /^gastan/i, noun: 'consumo de' },
    { verb: /^tardan/i, noun: 'demoras de' },
    { verb: /^requieren/i, noun: 'dependencia de' },
    { verb: /^reciben/i, noun: 'recepción de' },
    { verb: /^experimentan/i, noun: 'experiencia de' },
    { verb: /^invierten/i, noun: 'inversión de' },
    { verb: /^abandonan/i, noun: 'abandono de' },
    { verb: /^devuelven/i, noun: 'devolución de' },
    { verb: /^dudan/i, noun: 'dudas sobre' }
  ];
  
  let fixedProblem = problem;
  
  // Corregir verbos a sustantivos
  for (const fix of verbsToFix) {
    if (fix.verb.test(problem)) {
      fixedProblem = problem.replace(fix.verb, fix.noun);
      console.log(`🔧 Corrigiendo verbo a sustantivo: "${problem}" → "${fixedProblem}"`);
      break;
    }
  }
  
  return {
    group: group || 'usuarios no especificados',
    problem: fixedProblem || 'problemas no identificados',
    reason: reason || 'causas no determinadas'
  };
}

/**
 * 🤖 Genera sugerencias de hipótesis usando IA
 * @param {Object} context - Contexto del problema
 * @param {string} sector - Sector o industria
 * @returns {Promise<Object>} Componentes de hipótesis sugeridos
 */
export const generateHypothesisSuggestion = async (context = '', sector = 'universidad') => {
  console.log('🚀 [IA Service] Generando sugerencia de hipótesis:', { context, sector });
  
  try {
    // Intentar generar con IA real
    const { getAIResponse } = await import('./aiService.js');
    
    const prompt = `Devuelve ÚNICAMENTE un objeto JSON válido con componentes de hipótesis específicos para el contexto dado. No incluyas texto adicional.

Contexto del problema: ${context || 'problema de usuario no especificado'}
Industria/Sector: ${sector}

Genera componentes específicos y realistas. Formato: {"group": "grupo específico de usuarios", "problem": "problema observable concreto", "reason": "razón basada en evidencia o comportamiento medible"}

Evita generalidades. Cada componente debe ser específico y comprobable.`;

    const aiResponse = await getAIResponse('tutor', {
      hipotesis: prompt,
      labId: 'lab1',
      stepId: 'hipotesis_suggestion',
      sessionId: `suggestion_${Date.now()}`
    });

    // Intentar parsear respuesta de IA (compatible con contrato v1.0)
    try {
      // 🔍 DEBUG: Log completo de la respuesta IA
      console.log('🔍 [DEBUG] aiResponse completo:', aiResponse);
      console.log('🔍 [DEBUG] contract_version:', aiResponse?.contract_version);
      console.log('🔍 [DEBUG] content:', aiResponse?.content);
      console.log('🔍 [DEBUG] payload:', aiResponse?.payload);
      
      // 🎯 Detectar formato de contrato y extraer sugerencia correctamente
      let suggestion;
      
      if (aiResponse?.contract_version === "1.0") {
        console.log('🎯 [DEBUG] Usando contrato v1.0');
        // Contrato v1.0: la respuesta está en payload.output
        suggestion = aiResponse.payload?.output;
      } else {
        console.log('🎯 [DEBUG] Usando contrato legacy, parseando content:', typeof aiResponse?.content);
        // Contrato legacy: parsear content como JSON
        suggestion = JSON.parse(aiResponse.content);
      }
      
      if (suggestion && suggestion.group && suggestion.problem && suggestion.reason) {
        // 🔍 Validar y corregir la sugerencia de IA
        const validatedSuggestion = validateAndFixSuggestion(suggestion);
        console.log('✅ [IA Service] Sugerencia generada y validada:', validatedSuggestion);
        return {
          success: true,
          source: 'ai',
          suggestion: validatedSuggestion
        };
      }
    } catch (parseError) {
      console.warn('⚠️ [IA Service] Error parsing IA response, usando fallback:', parseError);
    }
    
  } catch (error) {
    console.warn('⚠️ [IA Service] Error generando sugerencia, usando fallback:', error);
  }

  // Fallback: múltiples variantes por sector para evitar repetición
  const fallbackSuggestions = {
    universidad: [
      {
        group: "estudiantes de último año de carreras de ciencias de la comunicación",
        problem: "dificultades para demostrar habilidades prácticas en sus CVs", 
        reason: "los cursos son mayormente teóricos y no incluyen proyectos reales con empresas"
      },
      {
        group: "estudiantes de posgrado que trabajan tiempo completo",
        problem: "tendencia a abandonar estudios antes del segundo semestre",
        reason: "las clases presenciales coinciden con horarios laborales y no hay modalidad flexible"
      },
      {
        group: "estudiantes de intercambio internacional en universidades peruanas",
        problem: "dificultades para integrarse socialmente con compañeros locales",
        reason: "no existen programas estructurados de mentoría cultural en las universidades"
      }
    ],
    retail: [
      {
        group: "madres millennials de 28-35 años que compran productos para bebés online",
        problem: "frustración al devolver el 30% de productos de cuidado infantil después de comprar",
        reason: "las descripciones no especifican rangos de edad exactos ni texturas del producto"
      },
      {
        group: "consumidores de productos orgánicos de ingresos medios-altos",
        problem: "dudas sobre la autenticidad de certificaciones orgánicas al comprar online",
        reason: "no pueden verificar la trazabilidad del producto desde su origen hasta la venta"
      },
      {
        group: "compradores de ropa deportiva de 18-25 años",
        problem: "abandono de compras online por incertidumbre en tallas",
        reason: "las guías de tallas varían entre marcas y no hay probadores virtuales"
      }
    ],
    salud: [
      {
        group: "adultos de 45-60 años con diabetes tipo 2 recién diagnosticada",
        problem: "dificultades para mantener niveles estables de glucosa en las primeras 8 semanas",
        reason: "reciben información nutricional genérica que no considera sus preferencias alimentarias"
      },
      {
        group: "cuidadores familiares de pacientes con demencia",
        problem: "agotamiento emocional en los primeros 6 meses de cuidado",
        reason: "no tienen acceso a grupos de apoyo cercanos ni entrenamiento específico"
      },
      {
        group: "profesionales de la salud en hospitales públicos",
        problem: "sobrecarga de 40% más tiempo en documentación que en atención directa",
        reason: "los sistemas de registro están fragmentados entre diferentes plataformas"
      }
    ],
    gobierno: [
      {
        group: "emprendedores que quieren formalizar su primer negocio",
        problem: "demoras de más de 30 días para obtener licencia de funcionamiento municipal",
        reason: "deben presentar documentos en diferentes oficinas sin coordinación entre departamentos"
      },
      {
        group: "ciudadanos adultos mayores que realizan trámites de pensión",
        problem: "dependencia de familiares para completar procedimientos digitales",
        reason: "las plataformas gubernamentales no están diseñadas para personas con baja alfabetización digital"
      },
      {
        group: "migrantes que buscan regularizar su situación migratoria",
        problem: "confusión por información contradictoria sobre requisitos en diferentes oficinas",
        reason: "no existe un protocolo unificado de información entre entidades gubernamentales"
      }
    ],
    tecnologia: [
      {
        group: "desarrolladores freelance de aplicaciones móviles",
        problem: "pérdida del 25% de clientes potenciales durante la negociación inicial",
        reason: "no tienen herramientas para crear propuestas técnicas y presupuestos estandarizados rápidamente"
      },
      {
        group: "equipos de startups tecnológicas de 3-8 personas",
        problem: "consumo de más de 12 horas semanales en reportes de progreso manual",
        reason: "usan herramientas disconnectadas que no integran automáticamente métricas de desarrollo"
      },
      {
        group: "administradores de sistemas de empresas medianas",
        problem: "demoras de 48-72 horas en detectar problemas de seguridad",
        reason: "monitorizan sistemas manualmente sin alertas automatizadas inteligentes"
      }
    ]
  };

  // Seleccionar variante aleatoria del sector
  const sectorSuggestions = fallbackSuggestions[sector] || fallbackSuggestions.universidad;
  const randomIndex = Math.floor(Math.random() * sectorSuggestions.length);
  const rawSuggestion = sectorSuggestions[randomIndex];
  
  // 🔍 Validar y corregir sugerencia de fallback también
  const suggestion = validateAndFixSuggestion(rawSuggestion);
  
  console.log(`📋 [IA Service] Usando sugerencia fallback para sector: ${sector}, variante: ${randomIndex + 1}/${sectorSuggestions.length}`);
  console.log('🎯 Sugerencia seleccionada y validada:', suggestion);
  
  return {
    success: true,
    source: 'fallback',
    suggestion,
    metadata: {
      sector,
      variant: randomIndex + 1,
      totalVariants: sectorSuggestions.length
    }
  };
};

/**
 * Mock para otros endpoints necesarios
 */
export const mockStartSession = async (labId, mode = 'individual') => {
  await new Promise(resolve => setTimeout(resolve, 500));
  
  const sessionId = `${labId}_${Date.now()}_${mode}`;
  
  const firstBlock = {
    "lab_id": labId,
    "step_id": "intro_1",
    "tipo_visual": "narrativa",
    "mensaje_coach": {
      "titulo": "Bienvenido a InnovaTech Lab 1",
      "cuerpo": "Mapearemos tu hipótesis central y el circuito Build–Measure–Learn para validar tu idea de negocio."
    },
    "opciones": [
      { "etiqueta": "Definir hipótesis", "valor": "definir_hipotesis", "icono": "lightbulb" },
      { "etiqueta": "Ver ejemplo", "valor": "ver_ejemplo", "icono": "book" }
    ],
    "mostrar_input_text": false
  };
  
  return { session_id: sessionId, first_block: firstBlock };
};

export const mockGetSession = async (sessionId) => {
  await new Promise(resolve => setTimeout(resolve, 300));
  
  return {
    session_id: sessionId,
    lab_id: 'lab1',
    step_id: 'intro_1',
    mode: 'individual',
    messages: [],
    evidences: [],
    status: 'active'
  };
};

// 📤 Exportar configuración para uso en componentes
export { AI_CONFIG };